import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import AccessDenied from "components/AccessDenied";

/*** Backend + navigation use `{ allowed: true }`; some paths may still use bare booleans. */
export const isPermissionAllowed = (permissions, key) => {
  const p = permissions?.[key];
  if (p === true) return true;
  if (p && typeof p === "object" && p.allowed === true) return true;
  return false;
};

const hasDashboardPermission = (permissions) =>
  isPermissionAllowed(permissions, "dashboard") ||
  isPermissionAllowed(permissions, "analytics");

/**
 * First route the user may open, matching sidebar order.
 * Admin/co_admin with an empty permissions object keeps full access (legacy).
 * When a permissions config exists, each module must be explicitly allowed.
 */
export const getFirstAccessibleRoute = (userRole, permissions = {}) => {
  const hasPermissionConfig =
    permissions && Object.keys(permissions).length > 0;

  // Company users default to products-management
  if (userRole === "Company") {
    return "/dashboard/products-management";
  }

  // Admin / co_admin with no permission config: full access → analytics
  if (
    (userRole === "Admin" || userRole === "co_admin") &&
    !hasPermissionConfig
  ) {
    return "/dashboard/analytics";
  }

  // Same order as navigation.js; use .allowed, not truthy objects
  if (hasDashboardPermission(permissions)) {
    return "/dashboard/analytics";
  }
  if (isPermissionAllowed(permissions, "appOrders")) {
    return "/dashboard/orders-management";
  }
  if (isPermissionAllowed(permissions, "talkToAgronomist")) {
    return "/dashboard/agronomist";
  }
  if (isPermissionAllowed(permissions, "bulkOrders")) {
    return "/dashboard/bulk-upload";
  }
  if (isPermissionAllowed(permissions, "returnCancelRequest")) {
    return "/dashboard/return-cancel-request";
  }
  if (isPermissionAllowed(permissions, "quotation")) {
    return "/dashboard/Quotation-proforma";
  }
  if (isPermissionAllowed(permissions, "farmerManagement")) {
    return "/dashboard/farmers";
  }
  if (isPermissionAllowed(permissions, "agriStore")) {
    return "/dashboard/products-management";
  }
  if (isPermissionAllowed(permissions, "groceries")) {
    return "/dashboard/groceries";
  }
  if (isPermissionAllowed(permissions, "farmerForum")) {
    return "/dashboard/posts";
  }
  if (isPermissionAllowed(permissions, "successStories")) {
    return "/dashboard/courses";
  }
  if (isPermissionAllowed(permissions, "eventType")) {
    return "/dashboard/event-types";
  }
  if (isPermissionAllowed(permissions, "cropType")) {
    return "/dashboard/crop-types";
  }
  if (isPermissionAllowed(permissions, "enquiry")) {
    return "/dashboard/enquiry-form";
  }
  if (isPermissionAllowed(permissions, "fieldVisit")) {
    return "/dashboard/field-visit";
  }
  if (isPermissionAllowed(permissions, "category")) {
    return "/dashboard/invoice";
  }
  if (isPermissionAllowed(permissions, "cropHub")) {
    return "/dashboard/crop-hub";
  }
  if (isPermissionAllowed(permissions, "diseaseAdvisory")) {
    return "/dashboard/disease-advisory";
  }
  if (isPermissionAllowed(permissions, "aiChatBot")) {
    return "/dashboard/ai-chat-bot";
  }
  if (isPermissionAllowed(permissions, "users")) {
    return "/dashboard/users";
  }
  if (isPermissionAllowed(permissions, "permissions")) {
    return "/dashboard/permissions";
  }
  if (
    isPermissionAllowed(permissions, "FAQs") ||
    isPermissionAllowed(permissions, "faq") ||
    isPermissionAllowed(permissions, "FAQ")
  ) {
    return "/dashboard/FAQ";
  }
  if (isPermissionAllowed(permissions, "report")) {
    return "/dashboard/report";
  }
  if (isPermissionAllowed(permissions, "transaction")) {
    return "/dashboard/transaction";
  }

  // Role with a config but no allowed modules: open analytics → AccessDenied (no content)
  if (userRole === "Admin" || userRole === "co_admin") {
    return "/dashboard/analytics";
  }

  return "/dashboard/products-management";
};

const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission = null }) => {
  const location = useLocation();
  // Re-read localStorage when Permissions tab updates the current role
  const [permissionVersion, setPermissionVersion] = useState(0);

  useEffect(() => {
    const refresh = () => setPermissionVersion((v) => v + 1);
    window.addEventListener("permissions-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("permissions-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  void permissionVersion;
  const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
  const userData = loggedData?.data;
  const isAuthenticated = !!userData;
  const userRole = userData?.user_type;
  const permissions = userData?.permissions || {};
  const hasPermissionConfig =
    permissions && Object.keys(permissions).length > 0;

  // If user is not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block Farmer role from accessing dashboard - clear localStorage and redirect
  if (userRole === "Farmer") {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }

  // If route requires specific roles and user's role is not included
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
    return <Navigate to={firstAccessibleRoute} replace />;
  }

  // If permission is required, check it (for all users including Admin/Company)
  if (requiredPermission) {
    // Dashboard / analytics — when Permission-tab config exists, Dashboard must be
    // explicitly allowed. Denied access shows Access Denied (no dashboard content).
    if (
      requiredPermission === "dashboard" ||
      requiredPermission === "analytics"
    ) {
      if (userRole === "Company") {
        // Company keeps default access to analytics
      } else if (userRole === "Admin" || userRole === "co_admin") {
        if (hasPermissionConfig && !hasDashboardPermission(permissions)) {
          return (
            <AccessDenied message="You do not have permission to access this page." />
          );
        }
      } else if (!hasDashboardPermission(permissions)) {
        return (
          <AccessDenied message="You do not have permission to access this page." />
        );
      }
    } else if (userRole === "Admin" || userRole === "co_admin") {
      // Admin has access by default, but honor explicit denials in permissions
      if (
        hasPermissionConfig &&
        !isPermissionAllowed(permissions, requiredPermission) &&
        !(
          requiredPermission === "FAQs" &&
          (isPermissionAllowed(permissions, "faq") ||
            isPermissionAllowed(permissions, "FAQ"))
        )
      ) {
        const firstAccessibleRoute = getFirstAccessibleRoute(
          userRole,
          permissions
        );
        // Avoid redirect loop when the only fallback is this same denied route
        if (
          location.pathname === firstAccessibleRoute ||
          (firstAccessibleRoute === "/dashboard/analytics" &&
            !hasDashboardPermission(permissions))
        ) {
          return <AccessDenied />;
        }
        return <Navigate to={firstAccessibleRoute} replace />;
      }
    } else if (userRole === "Company") {
      const companyAllowedPermissions = [
        "agriStore",
        "appOrders",
        "bulkOrders",
        "quotation",
        "enquiry",
        "farmerManagement",
        "talkToAgronomist",
        "fieldVisit",
        "diseaseAdvisory",
        "eventType",
        "cropType",
        "farmerForum",
        "successStories",
        "cropHub",
      ];
      if (
        !companyAllowedPermissions.includes(requiredPermission) &&
        !isPermissionAllowed(permissions, requiredPermission)
      ) {
        const firstAccessibleRoute = getFirstAccessibleRoute(
          userRole,
          permissions
        );
        return <Navigate to={firstAccessibleRoute} replace />;
      }
    } else {
      // Other roles (co_admin, developer, accounts, operations, etc.) need explicit permission
      let hasPermission;
      if (requiredPermission === "FAQs") {
        hasPermission =
          isPermissionAllowed(permissions, "FAQs") ||
          isPermissionAllowed(permissions, "faq") ||
          isPermissionAllowed(permissions, "FAQ");
      } else {
        hasPermission = isPermissionAllowed(
          permissions,
          requiredPermission
        );
      }

      if (!hasPermission) {
        const firstAccessibleRoute = getFirstAccessibleRoute(
          userRole,
          permissions
        );
        return <Navigate to={firstAccessibleRoute} replace />;
      }
    }
  }

  // Redirect from dashboard root to appropriate page based on permissions
  if (location.pathname === "/dashboard") {
    const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
    return <Navigate to={firstAccessibleRoute} replace />;
  }

  return children;
};

export default ProtectedRoute;
